import { useTranslation } from '../../../shared/i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Input, Tabs, Tag, Switch, Select, message, Space, Tooltip, Alert, Segmented, Badge } from 'antd';
import { SearchOutlined, SwapOutlined, AppstoreOutlined, ShoppingCartOutlined, DownloadOutlined, FilterOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import { userPath } from '../../../shared/config/paths';
import { formatMoney } from '../../../shared/utils/money';
import domainRegistrationService from '../../services/domainRegistrationService';
import { cartService } from '../../services/cartService';
import FilterBar from './components/search/FilterBar';
import ResultList from './components/search/ResultList';
import TldPriceTable from './components/search/TldPriceTable';
import { TldShowcase, Perks, ExactCard, BundleCard } from './components/search/Showcase';
import {
    parseDomainInput, isValidLabel, detectLanguage, filterTlds, labelVariations,
    resultState, resultKey, toCsv, downloadText, normalizeTld,
} from './components/search/searchUtils';
import './components/search/domainSearch.css';

const BATCH = 12;
// All on-sale TLDs; popular ones sort first (catalog.featured rank), the rest load on scroll
const DEFAULT_FILTERS = { category: 'all', price: 'all', language: null, idnOnly: false };
const MAX_BULK_LINES = 100;
const BUNDLE_ORDER = ['com', 'net', 'cn', 'xyz', 'org', 'top', 'io', 'co', 'app'];

const isBuyable = (r) => ['available', 'premium'].includes(resultState(r));
const rankState = (r) => (isBuyable(r) ? 0 : resultState(r) === 'taken' ? 1 : 2);

const DomainSearch = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tab = ['search', 'bulk', 'prices'].includes(searchParams.get('tab')) ? searchParams.get('tab') : 'search';

    // ---------- 目录 ----------
    const [catalog, setCatalog] = useState(null);
    const [catalogLoading, setCatalogLoading] = useState(true);
    const [filters, setFilters] = useState(DEFAULT_FILTERS);
    const [priceFilters, setPriceFilters] = useState(DEFAULT_FILTERS);

    useEffect(() => {
        let alive = true;
        domainRegistrationService.getTldDirectory({ view_type: 'general' })
            .then((res) => { if (alive && res?.success) setCatalog(res.data); })
            .catch(() => { if (alive) message.error(t('Failed to load suffix list')); })
            .finally(() => { if (alive) setCatalogLoading(false); });
        return () => { alive = false; };
    }, []);

    const byTld = useMemo(() => {
        const m = new Map();
        (catalog?.tlds || []).forEach((t) => {
            m.set(t.tld, t);
            if (t.tld_unicode) m.set(String(t.tld_unicode).toLowerCase(), t);
        });
        return m;
    }, [catalog]);
    const knownTlds = useMemo(() => new Set(byTld.keys()), [byTld]);
    // 失败记录没有 domain_unicode（形如 "测试.xn--fiqs8s"）：把后缀换成目录里的 unicode 形式
    const withDisplay = useCallback((r) => {
        if (!r || !r.domain) return r;
        const uni = r.domain_unicode || r.domain;
        const tld = String(r.tld || '').toLowerCase();
        const tu = byTld.get(tld)?.tld_unicode;
        if (tld && tu && tu !== tld && uni.toLowerCase().endsWith(`.${tld}`)) {
            return { ...r, domain_unicode: `${uni.slice(0, -tld.length)}${tu}` };
        }
        return r.domain_unicode ? r : { ...r, domain_unicode: uni };
    }, [byTld]);
    const filtered = useMemo(() => filterTlds(catalog, filters), [catalog, filters]);

    // ---------- 购物车 ----------
    const [inCart, setInCart] = useState(new Set());
    const [cartCount, setCartCount] = useState(0);
    const [adding, setAdding] = useState(new Set());
    const loadCart = useCallback(async () => {
        try {
            const res = await cartService.getCart();
            const items = res?.data?.items || [];
            const s = new Set();
            const domainItems = items.filter((i) => i.item_type === 'domain');
            setCartCount(domainItems.length);
            domainItems.forEach((i) => {
                [i.domain_name, i.metadata?.domain_name, i.metadata?.domain, i.product_name]
                    .filter(Boolean).forEach((d) => s.add(String(d).toLowerCase()));
            });
            setInCart(s);
        } catch { /* 未登录/空购物车 */ }
    }, []);
    useEffect(() => { loadCart(); }, [loadCart]);
    const cartHas = useCallback((r) => inCart.has(resultKey(r)) || inCart.has(String(r.domain_unicode || '').toLowerCase()), [inCart]);
    const inCartView = useMemo(() => ({ has: (k) => inCart.has(k) }), [inCart]);

    const addToCart = useCallback(async (r, { silent = false } = {}) => {
        const key = resultKey(r);
        setAdding((s) => new Set(s).add(key));
        try {
            const res = await cartService.addDomain(r.domain, 'register', 1);
            if (res?.success === false) throw res;
            setInCart((s) => {
                const n = new Set(s);
                n.add(key);
                if (r.domain_unicode) n.add(String(r.domain_unicode).toLowerCase());
                return n;
            });
            setCartCount((c) => c + 1);
            if (!silent) message.success(t('{{value}} added to cart', { value: r.domain_unicode || r.domain }));
            return true;
        } catch (e) {
            if (!silent) message.error(e?.error || e?.message || t('Failed to add to cart'));
            return false;
        } finally {
            setAdding((s) => { const n = new Set(s); n.delete(key); return n; });
        }
    }, []);
    const goTransfer = useCallback((r) => {
        navigate(userPath(`/domain-registration/transfer?domain=${encodeURIComponent(r.domain_unicode || r.domain)}`));
    }, [navigate]);

    // ---------- 单个搜索 ----------
    const [query, setQuery] = useState(searchParams.get('q') || '');
    const [mode, setMode] = useState('register');
    const [showFilters, setShowFilters] = useState(false);
    const [bundleBusy, setBundleBusy] = useState(false);
    const [run, setRun] = useState(null); // { label, exactTld, queue, offset, lang }
    const [results, setResults] = useState([]);
    const [pending, setPending] = useState([]);
    const [onlyAvailable, setOnlyAvailable] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const runId = useRef(0);

    const checkBatch = useCallback(async (label, tlds, id) => {
        setPending(tlds.map((t) => `${label}.${byTld.get(t)?.tld_unicode || t}`));
        try {
            const res = await domainRegistrationService.bulkCheckAvailability({ domains: [label], tlds, parallel: true });
            if (id !== runId.current) return;
            const rows = (Array.isArray(res?.data) ? res.data : []).map(withDisplay);
            setResults((prev) => {
                const seen = new Set(prev.map(resultKey));
                return [...prev, ...rows.filter((r) => !seen.has(resultKey(r)))];
            });
        } catch (e) {
            if (id === runId.current) message.error(e?.error || t('Query failed. Please try again.'));
        } finally {
            if (id === runId.current) setPending([]);
        }
    }, [byTld, withDisplay]);

    const startSearch = useCallback(async (raw, filtersOverride) => {
        const parsed = parseDomainInput(raw, knownTlds);
        if (!parsed) return;
        const label = parsed.label;
        if (!isValidLabel(label)) {
            message.warning(t('Domain names can only contain letters, numbers, hyphens, or characters such as Chinese'));
            return;
        }
        const lang = detectLanguage(label);
        let f = filtersOverride || filters;
        // 中文等非英文标签：自动按语种筛选后缀（只查能注册该语种的后缀）
        if (lang && f.language !== lang && catalog?.languages?.[lang]) {
            f = { ...f, language: lang, category: 'all' };
            setFilters(f);
            setShowFilters(true);
        }
        const exact = parsed.tld ? (byTld.get(normalizeTld(parsed.tld))?.tld || normalizeTld(parsed.tld)) : null;
        const ordered = filterTlds(catalog, f).map((t) => t.tld);
        const queue = exact ? [exact, ...ordered.filter((t) => t !== exact)] : ordered;
        if (!queue.length) {
            message.info(t('No TLDs match the current filters. Please broaden your filters.'));
            return;
        }

        const id = ++runId.current;
        setResults([]);
        setSuggestions([]);
        setRun({ label, exactTld: exact, queue, offset: BATCH, lang });
        const p = new URLSearchParams(searchParams);
        p.set('q', raw.trim());
        p.delete('tab');
        setSearchParams(p, { replace: true });

        await checkBatch(label, queue.slice(0, BATCH), id);

        // 相关建议：英文标签的简单变体，查首选后缀
        // eslint-disable-next-line no-control-regex
        if (id === runId.current && !/[^\x00-\x7f]/.test(label)) {
            const primary = exact || (byTld.has('com') ? 'com' : queue[0]);
            const vars = labelVariations(label, 6);
            if (vars.length && primary) {
                try {
                    const res = await domainRegistrationService.bulkCheckAvailability({ domains: vars, tlds: [primary], parallel: true });
                    if (id === runId.current) setSuggestions((Array.isArray(res?.data) ? res.data : []).filter(isBuyable));
                } catch { /* 建议失败不打扰用户 */ }
            }
        }
    }, [byTld, catalog, checkBatch, filters, knownTlds, searchParams, setSearchParams]);

    const loadMore = () => {
        if (!run || pending.length) return;
        const next = run.queue.slice(run.offset, run.offset + BATCH);
        if (!next.length) return;
        setRun({ ...run, offset: run.offset + BATCH });
        checkBatch(run.label, next, runId.current);
    };

    // 滚动到列表底部自动加载下一批后缀
    const moreRef = useRef(null);
    const loadMoreRef = useRef(loadMore);
    loadMoreRef.current = loadMore;
    useEffect(() => {
        const el = moreRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) loadMoreRef.current();
        }, { rootMargin: '300px 0px' });
        io.observe(el);
        return () => io.disconnect();
    }, [run, pending.length]);

    // 改筛选后自动重查当前词
    const onFiltersChange = (f) => {
        setFilters(f);
        if (run && tab === 'search') startSearch(query, f);
    };

    // 从 URL 带入的 q
    const autoRan = useRef(false);
    useEffect(() => {
        if (!autoRan.current && catalog && query && tab === 'search') {
            autoRan.current = true;
            startSearch(query);
        }
    }, [catalog]); // eslint-disable-line react-hooks/exhaustive-deps

    const exactKey = useMemo(() => {
        if (!run?.exactTld) return null;
        const r = results.find((x) => x.tld === run.exactTld);
        return r ? resultKey(r) : null;
    }, [results, run]);

    const shownResults = useMemo(() => {
        const list = [...results].sort((a, b) => {
            const ea = resultKey(a) === exactKey ? 0 : 1;
            const eb = resultKey(b) === exactKey ? 0 : 1;
            if (ea !== eb) return ea - eb;
            return rankState(a) - rankState(b);
        });
        return onlyAvailable ? list.filter((r) => isBuyable(r) || resultKey(r) === exactKey) : list;
    }, [results, exactKey, onlyAvailable]);

    // 首选结果：精确后缀，否则 .com，否则第一个可注册
    const topPick = useMemo(() => {
        if (!run) return null;
        if (run.exactTld) return results.find((x) => x.tld === run.exactTld) || null;
        return results.find((x) => x.tld === 'com') || results.find(isBuyable) || null;
    }, [results, run]);
    const topKey = topPick ? resultKey(topPick) : null;
    const topPending = run && !topPick && pending.length ? pending[0] : null;
    const bundle = useMemo(() => {
        const pool = results.filter((r) => isBuyable(r) && resultKey(r) !== topKey && resultState(r) === 'available');
        const picked = [];
        BUNDLE_ORDER.forEach((t) => { const r = pool.find((x) => x.tld === t); if (r && picked.length < 3) picked.push(r); });
        return picked;
    }, [results, topKey]);
    const bundleInCart = bundle.length > 0 && bundle.every(cartHas);
    const addBundle = async () => {
        setBundleBusy(true);
        let ok = 0;
        for (const r of bundle.filter((x) => !cartHas(x))) {
            // eslint-disable-next-line no-await-in-loop
            if (await addToCart(r, { silent: true })) ok += 1;
        }
        setBundleBusy(false);
        if (ok) message.success(t('{{ok}} domains added', { ok: ok }));
    };
    // 「热门」标签只给最热的几个后缀，避免满屏都是
    const hotSet = useMemo(() => new Set((catalog?.featured || []).slice(0, 4).map((t) => t.tld)), [catalog]);
    const tldInfo = useCallback((t) => {
        const info = byTld.get(String(t || '').toLowerCase());
        return info ? { ...info, is_featured: hotSet.has(info.tld) } : info;
    }, [byTld, hotSet]);
    const tldInfoPlain = useCallback((t) => {
        const info = byTld.get(String(t || '').toLowerCase());
        return info ? { ...info, is_featured: false } : info;
    }, [byTld]);
    const listRows = useMemo(() => shownResults.filter((r) => resultKey(r) !== topKey), [shownResults, topKey]);

    const remaining = run ? Math.max(0, run.queue.length - run.offset) : 0;
    const buyableCount = results.filter(isBuyable).length;
    const allUnavailable = results.length > 0 && !pending.length && results.every((r) => resultState(r) === 'error');

    // ---------- 批量 ----------
    const [bulkText, setBulkText] = useState('');
    const [bulkTlds, setBulkTlds] = useState(['com', 'net', 'cn']);
    const [bulkResults, setBulkResults] = useState([]);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkSelected, setBulkSelected] = useState([]);
    const [bulkOnlyAvail, setBulkOnlyAvail] = useState(false);
    const [bulkAdding, setBulkAdding] = useState(false);

    const runBulk = async () => {
        const lines = bulkText.split(/[\n,，;；\s]+/).map((l) => l.trim()).filter(Boolean).slice(0, MAX_BULK_LINES);
        if (!lines.length) { message.warning(t('Please enter domains, one per line')); return; }
        const tlds = bulkTlds.filter((t) => byTld.has(t));
        const full = [];
        lines.forEach((line) => {
            const p = parseDomainInput(line, knownTlds);
            if (!p || !isValidLabel(p.label)) return;
            if (p.tld) full.push(`${p.label}.${byTld.get(normalizeTld(p.tld))?.tld || p.tld}`);
            else tlds.forEach((t) => full.push(`${p.label}.${t}`));
        });
        const uniq = [...new Set(full)];
        if (!uniq.length) { message.warning(tlds.length ? t('No valid domains') : t('Please select at least one TLD')); return; }
        setBulkLoading(true);
        setBulkResults([]);
        setBulkSelected([]);
        try {
            for (let i = 0; i < uniq.length; i += 30) {
                // eslint-disable-next-line no-await-in-loop
                const res = await domainRegistrationService.bulkCheckAvailability({ domains: uniq.slice(i, i + 30), parallel: true });
                const rows = (Array.isArray(res?.data) ? res.data : []).map(withDisplay);
                setBulkResults((prev) => [...prev, ...rows]);
            }
        } catch (e) {
            message.error(e?.error || t('Query failed. Please try again.'));
        } finally {
            setBulkLoading(false);
        }
    };

    const bulkShown = useMemo(() => {
        const l = [...bulkResults].sort((a, b) => rankState(a) - rankState(b));
        return bulkOnlyAvail ? l.filter(isBuyable) : l;
    }, [bulkResults, bulkOnlyAvail]);

    const addSelected = async () => {
        const rows = bulkResults.filter((r) => bulkSelected.includes(resultKey(r)) && isBuyable(r) && !cartHas(r));
        if (!rows.length) return;
        setBulkAdding(true);
        let ok = 0;
        for (const r of rows) {
            // eslint-disable-next-line no-await-in-loop
            if (await addToCart(r, { silent: true })) ok += 1;
        }
        setBulkAdding(false);
        setBulkSelected([]);
        if (ok === rows.length) message.success(t('{{ok}} domains added', { ok: ok }));
        else message.warning(t('{{ok}} added, {{value2}} failed', { ok: ok, value2: rows.length - ok }));
    };

    // ---------- 公共 ----------
    const setTab = (k) => {
        const p = new URLSearchParams(searchParams);
        if (k === 'search') p.delete('tab'); else p.set('tab', k);
        setSearchParams(p, { replace: true });
    };
    const pickTld = (item) => {
        const label = run?.label || parseDomainInput(query, knownTlds)?.label;
        setTab('search');
        if (label) {
            const q = `${label}.${byTld.get(item)?.tld_unicode || item}`;
            setQuery(q);
            startSearch(q);
        } else {
            setQuery(`.${byTld.get(item)?.tld_unicode || item}`);
            message.info(t('Enter your desired name before the suffix'));
        }
    };

    const popularStrip = (catalog?.featured || []).filter((t) => t.available !== false).slice(0, 12);
    // 强制注册商一个后缀都不售（销售暂停）→ 一行提示，而不是让用户搜完才发现
    const salesClosed = !!catalog && (catalog.tlds || []).length > 0 && !(catalog.tlds || []).some((t) => t.available !== false);

    const onModeChange = (m) => {
        if (m === 'transfer') {
            const d = query.trim();
            navigate(userPath(`/domain-registration/transfer${d ? `?domain=${encodeURIComponent(d)}` : ''}`));
            return;
        }
        if (m === 'renew') {
            navigate(userPath('/domain-registration/my-domains'));
            return;
        }
        setMode(m);
    };

    const showcase = (catalog?.featured || []).filter((t) => t.available !== false && t.registration_price != null).slice(0, 8);

    const searchTab = (
        <div>
            <div className={`ds-hero2 ${run ? 'is-compact' : ''}`}>
                {!run && <div className="ds-hero2-title">{t('Find your domain')}</div>}
                <Segmented
                    className="ds-mode"
                    value={mode}
                    onChange={onModeChange}
                    options={[{ value: 'register', label: t('Register') }, { value: 'transfer', label: t('Transfer in') }, { value: 'renew', label: t('Renew') }]}
                />
                <div className="ds-search-row">
                    <Input
                        size="large"
                        allowClear
                        autoFocus
                        prefix={<SearchOutlined style={{ color: 'var(--text-3)' }} />}
                        placeholder={t('Enter a name or full domain, e.g. yeap or 测试.com')}
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onPressEnter={() => startSearch(query)}
                        aria-label={t('Domains')}
                    />
                    <Tooltip title={t('Filter extensions')}>
                        <Badge dot={filters.category !== 'all' || filters.price !== 'all' || !!filters.language || filters.idnOnly}>
                            <Button size="large" className="ds-filter-btn" icon={<FilterOutlined />} type={showFilters ? 'primary' : 'default'} ghost={showFilters} onClick={() => setShowFilters((v) => !v)} aria-label={t('Filter')} />
                        </Badge>
                    </Tooltip>
                    <Button size="large" type="primary" className="ds-go" onClick={() => startSearch(query)} disabled={!catalog}>
                        {t('Search')}
                    </Button>
                </div>
                {!run && popularStrip.length > 0 && (
                    <div className="ds-hint">
                        {popularStrip.map((t) => {
                            const promo = t.promo && Number(t.promo.price) < Number(t.registration_price);
                            return (
                                <span key={t.tld} className={`ds-chip2 ${promo ? 'is-promo' : ''}`} onClick={() => pickTld(t.tld)} role="button" tabIndex={0}>
                                    .{t.tld_unicode} <b>{formatMoney(promo ? t.promo.price : t.registration_price, t.currency)}</b>
                                </span>
                            );
                        })}
                    </div>
                )}
                {showFilters && (
                    <div className="ds-hero2-filters">
                        <FilterBar catalog={catalog} filters={filters} onChange={onFiltersChange} count={catalog ? filtered.length : null} />
                    </div>
                )}
            </div>

            {!run && (
                <>
                    {showcase.length > 0 && <div className="ds-section-title">{t('Featured extensions')}</div>}
                    <TldShowcase items={showcase} onPick={pickTld} />
                    <div className="ds-section-title">{t('Included with every domain')}</div>
                    <Perks />
                </>
            )}

            {run && (
                <>
                    {allUnavailable && !salesClosed && (
                        <Alert type="info" showIcon style={{ margin: '16px 0 0' }} message={t('Domain registration is not yet available. Please try again later or contact support.')} />
                    )}
                    <div style={{ marginTop: 16 }}>
                        <ExactCard
                            r={topPick}
                            pending={topPending}
                            tldUnicode={topPick ? tldInfo(topPick.tld)?.tld_unicode : null}
                            inCart={inCartView}
                            adding={topPick ? adding.has(resultKey(topPick)) : false}
                            onAdd={addToCart}
                            onTransfer={goTransfer}
                            onCheckout={() => navigate(userPath('/billing/cart'))}
                        />
                    </div>
                    <BundleCard
                        rows={bundle}
                        label={run.label}
                        tldOf={(r) => tldInfo(r.tld)?.tld_unicode || r.tld}
                        onAddAll={addBundle}
                        busy={bundleBusy}
                        allInCart={bundleInCart}
                    />
                    <div className="ds-section-title">
                        <span>
                            {t('More TLDs')}
                            <span className="ds-sub" style={{ marginLeft: 8 }}>
                                {t('Available to register: {{count}} / {{total}}', { count: buyableCount, total: results.length })}
                            </span>
                        </span>
                        <Space size={8}>
                            <span className="ds-sub">{t('Show available domains only')}</span>
                            <Switch size="small" checked={onlyAvailable} onChange={setOnlyAvailable} />
                        </Space>
                    </div>
                    <ResultList
                        rows={listRows}
                        pendingKeys={topPending ? pending.slice(1) : pending}
                        inCart={inCartView}
                        adding={adding}
                        onAdd={addToCart}
                        onTransfer={goTransfer}
                        tldInfo={tldInfo}
                    />
                    {!pending.length && results.length === 0 && <div className="ds-empty">{t('No results')}</div>}
                    {remaining > 0 && (
                        <div ref={moreRef} className="ds-more">
                            {pending.length > 0 ? t('Checking more extensions…') : <Button type="link" onClick={loadMore}>{t('{{remaining}} more extensions', { remaining })}</Button>}
                        </div>
                    )}
                    {suggestions.length > 0 && (
                        <>
                            <div className="ds-section-title">{t('You may also like')}</div>
                            <ResultList rows={suggestions} inCart={inCartView} adding={adding} onAdd={addToCart} tldInfo={tldInfoPlain} />
                        </>
                    )}
                </>
            )}
        </div>
    );

    const bulkTldOptions = useMemo(() => (catalog?.tlds || []).map((t) => ({
        value: t.tld,
        label: `.${t.tld_unicode}`,
    })), [catalog]);

    const bulkTab = (
        <div>
            <div className="ds-hero">
                <Input.TextArea
                    rows={6}
                    value={bulkText}
                    onChange={(e) => setBulkText(e.target.value)}
                    placeholder={t('One per line, up to 100\nyeap\nexample.com')}
                    aria-label={t('Bulk domains')}
                />
                <div className="ds-filters">
                    <Tooltip title={t('Names entered without an extension will be searched with these extensions')}>
                        <span className="ds-sub" style={{ color: 'var(--text-2)' }}>{t('Suffix')}</span>
                    </Tooltip>
                    <Select
                        mode="multiple"
                        value={bulkTlds}
                        onChange={setBulkTlds}
                        options={bulkTldOptions}
                        maxTagCount="responsive"
                        style={{ flex: 1, minWidth: 220 }}
                        showSearch
                        optionFilterProp="label"
                    />
                    <Button type="primary" onClick={runBulk} loading={bulkLoading} disabled={!catalog}>{t('Query')}</Button>
                </div>
            </div>
            {(bulkResults.length > 0 || bulkLoading) && (
                <>
                    <div className="ds-section-title">
                        <span>
                            {t('Result')}<span className="ds-sub" style={{ marginLeft: 8 }}>{t('{{total}} total · {{count}} available to register', { total: bulkResults.length, count: bulkResults.filter(isBuyable).length })}</span>
                        </span>
                        <Space size={8} wrap>
                            <span className="ds-sub">{t('Show available domains only')}</span>
                            <Switch size="small" checked={bulkOnlyAvail} onChange={setBulkOnlyAvail} />
                            <Button size="small" onClick={() => setBulkSelected(bulkResults.filter((r) => isBuyable(r) && !cartHas(r)).map(resultKey))}>{t('Select all available domains')}</Button>
                            <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadText(toCsv(bulkShown), `domains_${Date.now()}.csv`)}>{t('Export')}</Button>
                            <Button size="small" type="primary" icon={<ShoppingCartOutlined />} disabled={!bulkSelected.length} loading={bulkAdding} onClick={addSelected}>
                                {bulkSelected.length ? t('Add to cart ({{count}})', { count: bulkSelected.length }) : t('Add to cart')}
                            </Button>
                        </Space>
                    </div>
                    <ResultList
                        rows={bulkShown}
                        pendingKeys={bulkLoading ? ['…'] : []}
                        inCart={inCartView}
                        adding={adding}
                        onAdd={addToCart}
                        onTransfer={goTransfer}
                        tldInfo={tldInfo}
                        selectable
                        selected={bulkSelected}
                        onSelect={setBulkSelected}
                    />
                </>
            )}
        </div>
    );

    return (
        <div>
            <PageHeader
                title={t('Register a domain')}
                extra={(
                    <>
                        <Button icon={<AppstoreOutlined />} onClick={() => navigate(userPath('/domain-registration/my-domains'))}>{t('My domains')}</Button>
                        <Button icon={<SwapOutlined />} onClick={() => navigate(userPath('/domain-registration/transfer'))}>{t('Domain transfer')}</Button>
                    </>
                )}
            />
            {salesClosed && <Alert type="info" showIcon style={{ marginBottom: 12 }} message={t('Domain registration is not yet available')} />}
            <Tabs
                activeKey={tab}
                onChange={setTab}
                items={[
                    { key: 'search', label: t('Search'), children: searchTab },
                    { key: 'bulk', label: t('Bulk lookup'), children: bulkTab },
                    {
                        key: 'prices',
                        label: t('Price list'),
                        children: (
                            <TldPriceTable
                                catalog={catalog}
                                loading={catalogLoading}
                                filters={priceFilters}
                                onFiltersChange={setPriceFilters}
                                onPick={pickTld}
                            />
                        ),
                    },
                ]}
            />
            {cartCount > 0 && (
                <div className="ds-cartbar">
                    <span>{t('Your cart contains')} <b>{cartCount}</b> {t('domains')}</span>
                    <Button type="primary" icon={<ShoppingCartOutlined />} onClick={() => navigate(userPath('/billing/cart'))}>{t('Checkout')}</Button>
                </div>
            )}
        </div>
    );
};

export default DomainSearch;
